
import { saveAs } from 'file-saver';
import { changeDpiBlob } from 'changedpi'

export function downloadImage(url, filename) {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);

        resolve(URL.createObjectURL(blob))
      })
        .catch(error => console.error('下载失败:', error));
  })
}
/**
 * 路径匹配器
 * @param {string} pattern
 * @param {string} path
 * @returns {Boolean}
 */
export function isPathMatch(pattern, path) {
  const regexPattern = pattern
    .replace(/\//g, '\\/')
    .replace(/\*\*/g, '.*')
    .replace(/\*/g, '[^\\/]*')
  const regex = new RegExp(`^${regexPattern}$`)
  return regex.test(path)
}

/**
 * 判断value字符串是否为空
 * @param {string} value
 * @returns {Boolean}
 */
export function isEmpty(value) {
  if (value === null || value === undefined || value === 'undefined' || value.trim() === '') {
    return true
  }
  return false
}

/**
 * 校验面包栏长度
 * @param {string} value
 * @returns {Boolean}
 */
export function valBreadcrumb(value) {
  if (value.length > 5) {
    let levelList1 = value.slice(0, 2)
    let levelList2 = value[2]
    levelList2.title = '...'
    let levelList3 = value.slice(-2)
    let levelList = [...levelList1, levelList2, ...levelList3]
    return levelList
  }
  return value
}

/**
 * 获取文件预览图路径
 * @param value
 */
export async function previewList(list) {
  for (const item of list) {
    if (item.type === 2) {
      item.previewUrl = item.url
    }
    if (item.type === 8) {
      try {
        item.previewUrl = await parsePSDToPreview(item.url)
      } catch (e) {
        console.error('PSD 解析失败:', item.url, e)

        item.previewUrl = '' // 给个默认值，避免报错
      }
    }
    if (item.type === 9) {
      try {
        item.previewUrl = await parseAIToPreview(item.url)
      } catch (e) {
        console.error('AI 解析失败:', item.url, e)
        item.previewUrl = '' // 给个默认值，避免报错
      }
    }
    if (item.type === 11) {
      try {
        item.previewUrl = await parseTifToPreview(item.url)
      } catch (e) {
        console.error('tif 解析失败:', item.url, e)

        item.previewUrl = '' // 给个默认值，避免报错
      }
    }
  }
  return list
}

/**
 * 解析 PSD 文件并返回预览图像的 Data URL
 */
export async function parsePSDToPreview(psdUrl) {
  try {
    const PSD = (await import('psd.js')).default
    // 1. 使用 PSD.fromURL 直接加载（如果服务器支持 CORS）
    const psd = await PSD.fromURL(psdUrl)
    // 2. 检查是否有可导出的图像数据
    if (!psd.image || !psd.image.pixelData) {
      throw new Error('PSD 文件没有可导出的图像数据')
    }
    let width = psd.header.width
    let height = psd.header.height
    // 3. 将像素数据转换为 ImageData
    const { pixelData } = psd.image
    const imageData = new ImageData(new Uint8ClampedArray(pixelData), // 像素数据
      width, height)

    // 4. 渲染到 Canvas 并获取 Data URL
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.putImageData(imageData, 0, 0)

    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('PSD 解析失败:', error)
    throw error
  }
}

/**
 * 解析tif 文件并返回预览图像的 Data URL
 */
export async function parseTifToPreview(tifUrl) {
  try {
    const UTIF = (await import('utif')).default;

    const response = await fetch(tifUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();

    // 先解析 IFD 目录
    const ifds = UTIF.decode(arrayBuffer);
    if (!ifds || ifds.length === 0) {
      throw new Error('No images found in TIFF file');
    }

    const ifd = ifds[0];

    // 必须显式解码像素数据
    UTIF.decodeImage(arrayBuffer, ifd);

    // 转 RGBA8
    const rgba = UTIF.toRGBA8(ifd);

    const width = ifd.width;
    const height = ifd.height;

    if (!width || !height || !rgba) {
      throw new Error('Invalid TIFF data after decode');
    }

    // 绘制到 Canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height);
    ctx.putImageData(imageData, 0, 0);

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('TIFF解析失败:', error);
    return '';
  }
}

/**
 * 解析AI 文件并返回预览图像的 Data URL
 */
export async function parseAIToPreview(aiUrl) {
  if (!process.client) return '';
  try {
    // 检查 AI 是否内嵌 PDF
    const response = await fetch(aiUrl, { headers: { Range: "bytes=0-2048" } });
    if (!response.ok) throw new Error(`请求失败: ${response.status}`);
    const buffer = await response.arrayBuffer();
    const text = new TextDecoder("utf-8").decode(buffer);
    if (!text.includes("%PDF-")) return '';

    // 动态导入 pdfjs-dist（非 webpack 入口）
    const { getDocument, GlobalWorkerOptions } = window.pdfjsLib;

    // 手动创建 Worker 路径
    GlobalWorkerOptions.workerSrc = "/pdf.worker.js"; // 在 static/ 目录下放 pdf.worker.js

    // 渲染第一页
    const loadingTask = getDocument(aiUrl);
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1.0 });

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");

    await page.render({ canvasContext: ctx, viewport }).promise;

    return canvas.toDataURL("image/png");
  } catch (err) {
    console.error("AI 文件预览失败：", err);
    return '';
  }
}


/**
 * 获取文件类型
 * @param type  1文件夹 2图片 3视频 4word 5xls 6ppt 7pdf 8psd 9ai 10zip
 */
export function getFileType(type) {
  if (type === 1) {
    return '文件夹'
  } else if (type === 2) {
    return '图片'
  } else if (type === 3) {
    return '视频'
  } else if (type === 4) {
    return 'Word文档'
  } else if (type === 5) {
    return 'Excel表格'
  } else if (type === 6) {
    return 'PPT演示稿'
  } else if (type === 7) {
    return 'PDF文件'
  } else if (type === 8) {
    return 'PSD文件'
  } else if (type === 9) {
    return 'AI文件'
  } else if (type === 10) {
    return '压缩包'
  } else if (type === 11) {
    return 'TIF文件'
  } else if (type === 12) {
    return 'CDR文件'
  } else {
    return '未知文件'
  }
}

/**
 * 获取文件夹名称
 */
export function getFileName(list) {
  if (list.length > 0) {
    let nameList = list.filter((obj) => obj.type === 1).map((obj) => obj.name)
    let counter = 1
    if (!nameList.includes('新建文件夹')) {
      return '新建文件夹'
    }
    let fileName = '新建文件夹 (' + counter + ')'
    while (true) {
      // 判断条件
      if (!nameList.includes(fileName)) {
        break // 跳出循环
      }
      counter++
      fileName = '新建文件夹 (' + counter + ')'
    }
    return fileName
  } else {
    return '新建文件夹'
  }
}

/**
 * 校验文件夹名称
 */
export function fileNameVerify(value) {
  if (isEmpty(value)) {
    return '文件夹名称不能为空'
  }
  if (/[\\/:*?"<>|]/.test(value)) {
    return '文件夹名称不能包含\\ / : * ? " < > |等字符！'
  }
  return false
}

/**
 * 匹配文件后缀
 * @param fileName
 * @returns {string|boolean}
 */
export function matchFileSuffixType(fileName) {
  // 后缀获取
  let suffix = ''
  // 获取类型结果
  let result = ''
  try {
    let fileArr = fileName.split('.')
    if (fileArr.length > 1) {
      suffix = fileArr[fileArr.length - 1]
    }
  } catch (err) {
    suffix = ''
  }
  // 文件夹
  if (!suffix) {
    return 1
  }
  //类型转小写
  suffix = suffix.toUpperCase().toLowerCase()
  // 图片格式
  let imglist = ['png', 'jpg', 'jpeg', 'bmp', 'webp']
  result = imglist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 2
  }

  // 匹配 视频
  let videolist = ['mp4', 'm2v', 'mkv']
  result = videolist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 3
  }
  // 匹配word文件
  let doclist = ['doc', 'docx']
  result = doclist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 4
  }
  // 匹配xls文件
  let xlslist = ['xls', 'xlsx']
  result = xlslist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 5
  }
  // 匹配ppt文件
  let pptlist = ['ppt']
  result = pptlist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 6
  }
  // 匹配pdf文件
  let pdflist = ['pdf']
  result = pdflist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 7
  }
  // 匹配psd文件
  let psdlist = ['psd']
  result = psdlist.some(function (item) {
    return item === suffix
  })

  if (result) {
    return 8
  }
  // 匹配ai文件
  let ailist = ['ai']
  result = ailist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 9
  }
  // 匹配压缩文件
  let ziplist = ['zip', 'rar', 'jar', 'tar', 'gzip']
  result = ziplist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 10
  }
  // 匹配tif文件
  let tiflist = ['tif', 'tiff']
  result = tiflist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 11
  }
  // 匹配cdr文件
  let cdrlist = ['cdr']
  result = cdrlist.some(function (item) {
    return item === suffix
  })
  if (result) {
    return 12
  }
  //其他文件
  return 13
}

export function downloadFile(content, fileName = '', is_url = 1) {
  var url = ''
  if (is_url === 0) {
    saveAs(content, fileName)
  } else {
    url = content
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = url
    document.body.appendChild(iframe)

    // 自动清理 iframe，防止 DOM 污染
    setTimeout(() => {
      document.body.removeChild(iframe)
    }, 5000)
  }
}

export function parsePNGDPI(base64) {
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, '')
  const binary = atob(base64Data)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  // PNG签名（8字节）
  const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10]
  for (let i = 0; i < 8; i++) {
    if (bytes[i] !== PNG_SIGNATURE[i]) {
      return 0 // 无效PNG文件
    }
  }

  let offset = 8

  // 遍历所有数据块
  while (offset < bytes.length) {
    // 读取块长度（4字节大端序）
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]
    offset += 4

    // 读取块类型（4字节ASCII）
    const chunkType = String.fromCharCode(
      bytes[offset],
      bytes[offset + 1],
      bytes[offset + 2],
      bytes[offset + 3]
    )
    offset += 4

    // 处理pHYs块（包含DPI信息）
    if (chunkType === 'pHYs') {
      const pixelsPerUnitX =
        (bytes[offset] << 24) |
        (bytes[offset + 1] << 16) |
        (bytes[offset + 2] << 8) |
        bytes[offset + 3]

      // const pixelsPerUnitY =
      //    (bytes[offset + 4] << 24) |
      //    (bytes[offset + 5] << 16) |
      //    (bytes[offset + 6] << 8) |
      //    bytes[offset + 7]

      const unit = bytes[offset + 8] // 0=未知单位, 1=米

      if (unit === 1) {
        // 转换为DPI（像素/英寸）
        const dpiX = Math.round(pixelsPerUnitX * 0.0254)
        //const dpiY = Math.round(pixelsPerUnitY * 0.0254)
        return dpiX
      }
    }

    // 移动到下一个块（跳过当前块数据 + 4字节CRC）
    offset += length + 4
  }

  return 0 // 未找到DPI信息
}


export function parseJPGDPI(base64) {
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, '')
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  // 校验JPG文件头
  if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null;

  let offset = 2;

  while (offset < bytes.length - 1) {
    // 跳过填充字节
    if (bytes[offset] !== 0xFF) {
      offset++;
      continue;
    }

    const marker = bytes[offset + 1];
    const length = (bytes[offset + 2] << 8) | bytes[offset + 3];

    // 关键：使用length计算段结束位置
    const segmentEnd = offset + 2 + length;

    // 边界检查
    if (segmentEnd > bytes.length) break;

    // 仅处理APP0段
    if (marker === 0xE0) {
      // 检查标识符 (5字节:"JFIF\0")
      let identifier = "";
      for (let i = 4; i < 9; i++) {
        if (offset + i >= bytes.length) break;
        identifier += String.fromCharCode(bytes[offset + i]);
      }

      if (identifier === "JFIF" && offset + 15 < bytes.length) {
        const unit = bytes[offset + 11];
        const densityX = (bytes[offset + 12] << 8) | bytes[offset + 13];
        //const densityY = (bytes[offset + 14] << 8) | bytes[offset + 15];

        if (unit === 1) return densityX;
        if (unit === 2) return Math.round(densityX * 2.54);
        //if (unit === 1) return { dpiX: densityX, dpiY: densityY };
        //if (unit === 2) return {
        //   dpiX: Math.round(densityX * 2.54),
        //    dpiY: Math.round(densityY * 2.54)
        // };
      }
    } else if (marker === 0xE1) {
      // APP1段（可能是EXIF）
      const exifData = bytes.slice(offset + 4, offset + 2 + length);
      // exifData 是APP1段的数据（不包括标记和长度，但包括6字节的"Exif\0\0"）
      // 因此，前6字节是"Exif\0\0"，接下来是TIFF头

      // 至少需要6+8=14字节
      if (exifData.length < 14) return 0;

      // 检查Exif标识
      if (String.fromCharCode(...exifData.slice(0, 4)) !== "Exif") return 0;
      if (exifData[4] !== 0 || exifData[5] !== 0) return 0;

      // 从第6字节开始是TIFF头
      const tiffHeader = exifData.slice(6, 14);
      // 字节顺序: 0-1字节
      const byteOrder = String.fromCharCode(tiffHeader[0], tiffHeader[1]);
      const isLittleEndian = byteOrder === "II";
      const isBigEndian = byteOrder === "MM";
      if (!isLittleEndian && !isBigEndian) return 0;

      // 读取2字节的固定值 (应该为0x002A或0x2A00)
      const fortyTwo = isLittleEndian ?
        (tiffHeader[3] << 8) | tiffHeader[2] :
        (tiffHeader[2] << 8) | tiffHeader[3];
      if (fortyTwo !== 0x2A) return 0;

      // 读取第一个IFD的偏移量（4字节）
      let ifd0Offset = isLittleEndian ?
        (tiffHeader[7] << 24) | (tiffHeader[6] << 16) | (tiffHeader[5] << 8) | tiffHeader[4] :
        (tiffHeader[4] << 24) | (tiffHeader[5] << 16) | (tiffHeader[6] << 8) | tiffHeader[7];

      // 第一个IFD通常从TIFF头后8字节开始，所以偏移量通常是8，但这里我们使用读到的偏移量
      // 注意：偏移量是相对于TIFF头开始的位置（即exifData[6]的位置为0）
      // 所以实际在exifData中的位置：6 + ifd0Offset

      let ifd0Start = 6 + ifd0Offset;
      if (ifd0Start >= exifData.length) return 0;

      // 读取IFD0中的条目数量（2字节）
      let entryCount = isLittleEndian ?
        (exifData[ifd0Start + 1] << 8) | exifData[ifd0Start] :
        (exifData[ifd0Start] << 8) | exifData[ifd0Start + 1];
      ifd0Start += 2;

      // 每个条目12字节
      if (ifd0Start + entryCount * 12 > exifData.length) return 0;

      let dpiX = null;
      let dpiY = null;

      for (let i = 0; i < entryCount; i++) {
        let entryStart = ifd0Start + i * 12;
        // 读取标签ID（2字节）
        let tag = isLittleEndian ?
          (exifData[entryStart + 1] << 8) | exifData[entryStart] :
          (exifData[entryStart] << 8) | exifData[entryStart + 1];

        // 读取数据类型（2字节）
        let dataType = isLittleEndian ?
          (exifData[entryStart + 3] << 8) | exifData[entryStart + 2] :
          (exifData[entryStart + 2] << 8) | exifData[entryStart + 3];

        // 读取值的数量（4字节）
        //let valueCount = isLittleEndian ?
        //   (exifData[entryStart + 7] << 24) | (exifData[entryStart + 6] << 16) | (exifData[entryStart + 5] << 8) | exifData[entryStart + 4] :
        //  (exifData[entryStart + 4] << 24) | (exifData[entryStart + 5] << 16) | (exifData[entryStart + 6] << 8) | exifData[entryStart + 7];

        // 最后4字节是值本身或指向值的偏移（如果值超过4字节）
        let valueOffset = isLittleEndian ?
          (exifData[entryStart + 11] << 24) | (exifData[entryStart + 10] << 16) | (exifData[entryStart + 9] << 8) | exifData[entryStart + 8] :
          (exifData[entryStart + 8] << 24) | (exifData[entryStart + 9] << 16) | (exifData[entryStart + 10] << 8) | exifData[entryStart + 11];

        // 我们只关心分辨率标签（0x011A和0x011B）
        if (tag === 0x011A) { // XResolution
          // 数据类型应为5（有理数，两个LONG，第一个分子，第二个分母）
          if (dataType === 5) {
            // 有理数的值存储在偏移位置（因为8字节，不能放在4字节里）
            let valuePosition = 6 + valueOffset; // 相对于exifData[0]
            if (valuePosition + 8 <= exifData.length) {
              let numerator = isLittleEndian ?
                (exifData[valuePosition + 3] << 24) | (exifData[valuePosition + 2] << 16) | (exifData[valuePosition + 1] << 8) | exifData[valuePosition] :
                (exifData[valuePosition] << 24) | (exifData[valuePosition + 1] << 16) | (exifData[valuePosition + 2] << 8) | exifData[valuePosition + 3];
              let denominator = isLittleEndian ?
                (exifData[valuePosition + 7] << 24) | (exifData[valuePosition + 6] << 16) | (exifData[valuePosition + 5] << 8) | exifData[valuePosition + 4] :
                (exifData[valuePosition + 4] << 24) | (exifData[valuePosition + 5] << 16) | (exifData[valuePosition + 6] << 8) | exifData[valuePosition + 7];
              if (denominator !== 0) {
                dpiX = Math.round(numerator / denominator);
              }
            }
          }
        } else if (tag === 0x011B) { // YResolution
          if (dataType === 5) {
            let valuePosition = 6 + valueOffset;
            if (valuePosition + 8 <= exifData.length) {
              let numerator = isLittleEndian ?
                (exifData[valuePosition + 3] << 24) | (exifData[valuePosition + 2] << 16) | (exifData[valuePosition + 1] << 8) | exifData[valuePosition] :
                (exifData[valuePosition] << 24) | (exifData[valuePosition + 1] << 16) | (exifData[valuePosition + 2] << 8) | exifData[valuePosition + 3];
              let denominator = isLittleEndian ?
                (exifData[valuePosition + 7] << 24) | (exifData[valuePosition + 6] << 16) | (exifData[valuePosition + 5] << 8) | exifData[valuePosition + 4] :
                (exifData[valuePosition + 4] << 24) | (exifData[valuePosition + 5] << 16) | (exifData[valuePosition + 6] << 8) | exifData[valuePosition + 7];
              if (denominator !== 0) {
                dpiY = Math.round(numerator / denominator);
              }
            }
          }
        }
      }

      // 如果两个都找到，返回
      if (dpiX !== null && dpiY !== null) {
        return dpiX;
      } else if (dpiX !== null) {
        return dpiX; // 假设Y和X相同
      } else if (dpiY !== null) {
        return dpiX; // 假设X和Y相同
      }
    }

    // 关键：使用length移动到下一段
    offset = segmentEnd;
  }

  return 0;
}


export function picMerSave(data) { 
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // 设置画布尺寸
    canvas.width = data.width
    canvas.height = data.height;
  
    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // 绘制背景
    ctx.fillStyle = data.background;
   // ctx.globalAlpha = 0
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  //绘制图片

    if (data.list.length > 0) { 
      for (var i in data.list) { 

        ctx.save();

        // 应用不同的transform和filter效果
        // 移动到图片位置
        //ctx.translate(data.list[i].x + data.list[i].width/2, data.list[i].y + data.list[i].height/2);
       // ctx.transform(1, 0, 0, 0, 0.1, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
         // 移回原点
       // ctx.translate(-data.list[i].width/2, -data.list[i].height/2);

        ctx.drawImage(data.list[i].img, data.list[i].x, data.list[i].y, data.list[i].width, data.list[i].height);
          // 恢复画布状态
        ctx.restore();
      
      }
       ctx.save();
    }

  setTimeout(() => {
   // resolve(canvas.toDataURL('image/png', 1))

    // 保存数据URL供下载使用
    canvas.toBlob(function (blob1) {
      changeDpiBlob(blob1,300).then((blob) => { 
        // 将blob转换为file
      const file = new File([blob], 'filename.png', {
        type: blob.type,
        lastModified: new Date().getTime()
      });
      resolve({
        url:URL.createObjectURL(blob),
        file:file
      }) ;
      })
      
      }, 'image/png');
   }, 500);
  })
}